import type {
  McpCredentialTypeType,
  McpMultiIdentityDiscoveryResultDto,
  UpsertMcpCredentialDto,
} from '@terra/shared-contracts-mcp-servers';
import type { AiApplicationMcpTransport } from '@terra/shared-contracts-ai-applications';

export type McpAuthTypeLabelKey =
  | 'authTypeNone'
  | 'authTypeBearer'
  | 'authTypeApiKey'
  | 'authTypeBasic'
  | 'authTypeOAuth';

export type McpAuthTypeTranslator = (key: McpAuthTypeLabelKey) => string;

export function getMcpCredentialAuthTypeLabel({
  credentialType,
  translate,
}: {
  credentialType: McpCredentialTypeType | 'OAUTH_DCR' | null | undefined;
  translate: McpAuthTypeTranslator;
}): string {
  switch (credentialType) {
    case 'BEARER':
      return translate('authTypeBearer');
    case 'API_KEY':
      return translate('authTypeApiKey');
    case 'BASIC':
      return translate('authTypeBasic');
    case 'OAUTH':
    case 'OAUTH_DCR':
      return translate('authTypeOAuth');
    case 'NONE':
      return translate('authTypeNone');
    default:
      return translate('authTypeNone');
  }
}

export function getMcpCredentialDtoAuthTypeLabel({
  credential,
  translate,
}: {
  credential: UpsertMcpCredentialDto | null;
  translate: McpAuthTypeTranslator;
}): string {
  if (!credential || credential.type === 'NONE') {
    return translate('authTypeNone');
  }

  return getMcpCredentialAuthTypeLabel({
    credentialType: credential.type,
    translate,
  });
}

export const splitLines = (value: string) =>
  value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

export const parseMcpEnv = (value: string): Record<string, string> => {
  const env: Record<string, string> = {};

  splitLines(value).forEach((line) => {
    const separatorIndex = line.indexOf('=');

    if (separatorIndex > 0) {
      env[line.slice(0, separatorIndex).trim()] = line
        .slice(separatorIndex + 1)
        .trim();
    }
  });

  return env;
};

export const parseMcpArgs = (value: string) =>
  splitLines(value).flatMap((line) =>
    line
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean),
  );

export const formatMcpEnv = (
  mcpEnv: Record<string, string> | null | undefined,
) => {
  if (!mcpEnv) {
    return '';
  }

  return Object.entries(mcpEnv)
    .map(([key, envValue]) => `${key}=${envValue}`)
    .join('\n');
};

export const getMcpConnectionDisplayUrl = ({ url }: { url: string }) => url;

export function isOAuthMcpCredentialType(
  credentialType: McpCredentialTypeType | 'OAUTH_DCR' | null | undefined,
): boolean {
  return credentialType === 'OAUTH' || credentialType === 'OAUTH_DCR';
}

export function getMcpTransportLabel({
  transport,
  translate,
  emptyValue,
}: {
  transport: AiApplicationMcpTransport | null | undefined;
  translate: (key: `transportOptions.${AiApplicationMcpTransport}`) => string;
  emptyValue: string;
}): string {
  return transport ? translate(`transportOptions.${transport}`) : emptyValue;
}

type McpConnectionTestTitleTranslator = (
  key: 'mcpTest.success' | 'mcpTest.partialSuccess',
  values: Record<string, number>,
) => string;

export function getMcpMultiIdentityConnectionTestTitle({
  result,
  translate,
}: {
  result: Pick<McpMultiIdentityDiscoveryResultDto, 'identities' | 'tools'>;
  translate: McpConnectionTestTitleTranslator;
}): string {
  if (result.identities.length > 1) {
    return translate('mcpTest.partialSuccess', {
      connected: result.identities.filter(
        (identity) => identity.connectionSuccess,
      ).length,
      total: result.identities.length,
      count: result.tools.length,
    });
  }

  return translate('mcpTest.success', { count: result.tools.length });
}
